import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  CATCHMENT_ID_ATTRIBUTE,
  GRID_ID_ATTRIBUTE,
  GridOptionItem,
  POLYGON_ID_ATTRIBUTE
} from "@/components/analysisSummary/filterByGrid";
import { IOptionItem } from "@/components/ui/dropdown/types";
import { DROPDOWN_ALL_VALUE } from "@/components/ui/dropdownMultiSelect";
import { SPATIAL_ANALYSIS_RESULT_DATASET_NAME } from "@/constants/dataset";
import { formatNumberWithMaxDecimal } from "@/helpers/numberHelpers";
import { RootState } from "@/store";
import {
  setSelectedAttribute,
  setSpatialAnalysisDatasetId
} from "@/store/slice/analysisSummary";
import { EOutputAnalysisType } from "@/types/spatial";

export const useAnalysisSummaryData = () => {
  const dispatch = useDispatch();

  const { datasets, visualizationOrder, rawDatasets } = useSelector(
    (state: RootState) => state.dataset
  );

  const { gridsSelected, selectedAttribute, generalSummary, isHasSummary } =
    useSelector((state: RootState) => state.analysisSummary);

  const handleAttributeChange = (option: IOptionItem | null) => {
    dispatch(setSelectedAttribute(option as IOptionItem));
  };

  const filteredAttribute = generalSummary?.data.attributes.filter(
    (attr) => attr.attribute_name !== "Score"
  );

  const attributeSource = isHasSummary
    ? generalSummary?.data.attributes
    : filteredAttribute;

  const ATTRIBUTE_OPTIONS: IOptionItem[] =
    attributeSource?.map((attribute) => ({
      label: attribute.attribute_name,
      value: attribute.attribute_name
    })) || [];

  const isCatchment = useMemo(
    () =>
      generalSummary?.data.attributes.some((attr) =>
        attr.rows.some((row) => !!row.catchment_id)
      ) || false,
    [generalSummary?.data.attributes]
  );

  const isPolygon = useMemo(
    () =>
      generalSummary?.data.attributes.some((attr) =>
        attr.rows.some((row) => !!row.polygon_id)
      ) || false,
    [generalSummary?.data.attributes]
  );

  const GRID_DATA =
    generalSummary?.data.attributes
      .find((attr) => attr.attribute_name === selectedAttribute.value)
      ?.rows.map((row) => ({
        name: row.catchment_id || row.polygon_id || row.grid_id || "",
        value: formatNumberWithMaxDecimal(row.value, 3)
      })) || [];

  // Prepare list of ordered and original datasets
  const orderedDatasets = useMemo(
    () => visualizationOrder.map((id) => datasets[id]),
    [visualizationOrder, datasets]
  );
  const spatialAnalysisDatasetId =
    orderedDatasets.find(
      (dataset) => dataset?.data?.name === SPATIAL_ANALYSIS_RESULT_DATASET_NAME
    )?.data?.id ?? ""; // spatial analysis result
  const analysisResultDataset = rawDatasets[spatialAnalysisDatasetId];

  const originalDatasets = useMemo(
    () =>
      Object.entries(rawDatasets)
        .filter(([id]) => id !== spatialAnalysisDatasetId)
        .map(([, dataset]) => dataset),
    [rawDatasets, spatialAnalysisDatasetId]
  );

  // Prepare grid options for dropdown
  const getLabelPrefix = (isGrid: boolean, isPolygon: boolean): string => {
    if (isGrid) return "Grid ID";
    if (isPolygon) return "Polygon ID";
    return "Catchment ID";
  };

  const gridValue: GridOptionItem[] = useMemo(() => {
    if (!analysisResultDataset) return [];

    const { columns, rows, dataset_id } = analysisResultDataset;

    // Find column indices
    const gridIndex = columns.findIndex(
      (col) => col.name === GRID_ID_ATTRIBUTE
    );
    const catchmentIndex = columns.findIndex(
      (col) => col.name === CATCHMENT_ID_ATTRIBUTE
    );
    const polygonIndex = columns.findIndex(
      (col) => col.name === POLYGON_ID_ATTRIBUTE
    );
    const scoreIndex = columns.findIndex((col) => col.name === "Score");

    if (gridIndex === -1 && catchmentIndex === -1 && polygonIndex === -1)
      return [];

    // Determine which index and label to use
    const isGrid = gridIndex !== -1;
    const isPolygon = polygonIndex !== -1;
    const labelPrefix = getLabelPrefix(isGrid, isPolygon);

    let idIndex = catchmentIndex;

    if (isGrid) idIndex = gridIndex;
    else if (isPolygon) idIndex = polygonIndex;

    return rows
      .map((row) => {
        const idValue = row[idIndex] ?? "";
        const typeSafeIdValue =
          typeof idValue === "string" ? idValue : String(idValue);

        const scoreValue = row[scoreIndex] ?? 0;
        const typeSafeScoreValue =
          typeof scoreValue === "number" ? scoreValue : 0;

        return {
          datasetId: dataset_id,
          label: `${labelPrefix}: ${typeSafeIdValue}`,
          id: typeSafeIdValue,
          value: typeSafeIdValue,
          score: typeSafeScoreValue
        };
      })
      .sort((a, b) => b.score - a.score);
  }, [analysisResultDataset]);

  useEffect(() => {
    dispatch(setSpatialAnalysisDatasetId(spatialAnalysisDatasetId));
  }, [spatialAnalysisDatasetId]);

  const isAllSelected = gridsSelected?.[0] === DROPDOWN_ALL_VALUE;

  const isSiteProfiling =
    analysisResultDataset?.spatial_aggregation_type ===
    EOutputAnalysisType.PROFILING;

  const getTypeSummary = () => {
    if (isCatchment) {
      return "Catchment";
    }
    if (isPolygon) {
      return "Polygon";
    }
    return "Grid";
  };

  return {
    ATTRIBUTE_OPTIONS,
    isCatchment,
    isPolygon,
    GRID_DATA,
    gridValue,
    spatialAnalysisDatasetId,
    handleAttributeChange,
    isAllSelected,
    isSiteProfiling,
    originalDatasets,
    getTypeSummary
  };
};
